/**
 * ParcelInspector — Slide-up panel for the active parcel.
 *
 * Shows: farm name (editable), SG number, province, area, entity link,
 * active templates, infrastructure within parcel, and sync-to-PIM action.
 */

import { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Edit3, Save, Landmark, Link2, Building2, Cpu,
  RefreshCw, ChevronDown, Search, Check,
  Calendar, MessageSquare, Eye, CheckCircle, Lightbulb,
  Leaf, Trash2,
} from 'lucide-react';
import { useMapStore } from '../MapContext';
import {
  useUpdateParcel,
  useAssignParcelEntity,
  useCreateMapperParcelGroup,
  useInfraWithinParcel,
  useInfrastructureTaxonomy,
  useMapParcel,
  useMapperParcelGroups,
  useCreateGeeJob,
  useParcelTwinState,
  useSyncParcelToPim,
  useUpdateMapperParcelGroup,
  useDeleteParcel,
} from '../api';
import { CATEGORY_COLORS, CATEGORY_LABELS } from '../types';
import type { InfraCategoryType, LandCategory } from '../types';
import { usePartyEntities } from '@/features/entity/api';
import { useEntityMemories } from '../../entity-memories/api';
import type { EntityMemory } from '../../entity-memories/api';
import { toast } from 'sonner';
import { useQuery } from '@tanstack/react-query';
import { cpFetch } from '@/lib/cpClient';
import { useAuthStore } from '@/store/authStore';
import { useSelectedAppId } from '@/hooks/useSelectedAppId';
import { useMapperWorkspace } from '../workspaceStore';
import { useFarmInfrastructureScan } from '../useFarmInfrastructureScan';

const ENTITY_REMINDER_EVENT = 'mapper:entity-reminder';

const MEMORY_TYPE_ICONS: Record<string, typeof Calendar> = {
  event: Calendar,
  conversation: MessageSquare,
  observation: Eye,
  decision: CheckCircle,
  recommendation: Lightbulb,
};

function formatRelativeTime(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diffMs = now - then;
  const seconds = Math.floor(diffMs / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(months / 12)}y ago`;
}

function useInfraTemplates() {
  const { token } = useAuthStore();
  const appId = useSelectedAppId();
  return useQuery({
    queryKey: ['templates', 'iku-infrastructure', appId],
    queryFn: async () => {
      const res = await cpFetch('/api/templates?ikuCategory=iku-infrastructure&limit=250');
      return (res?.templates ?? res?.items ?? []) as Array<{
        id: string;
        name: string;
        entity_type?: string;
        iku_category?: string;
      }>;
    },
    enabled: !!token && !!appId,
    staleTime: 60_000,
  });
}

export default function ParcelInspector() {
  const {
    activeParcel,
    setActiveParcel,
    parcelInspectorOpen,
    setParcelInspectorOpen,
    geeOverlay,
    setGeeOverlay,
    setParcelLayerScope,
    setSelectedInfrastructure,
  } = useMapStore();

  const { data: latestParcel } = useMapParcel(activeParcel?.id);
  const parcel = latestParcel || activeParcel;
  const updateParcel = useUpdateParcel();
  const assignEntity = useAssignParcelEntity();
  const createGeeJob = useCreateGeeJob();
  const syncToPim = useSyncParcelToPim();
  const deleteParcel = useDeleteParcel();
  const {
    workspace,
    saveFarm,
    removeFarm,
    hasSavedFarm,
    syncSavedFarm,
    setActiveFarm,
    setActiveFarmGroup,
  } = useMapperWorkspace();
  const { data: infraItems } = useInfraWithinParcel(parcel?.id);
  const { data: parcelTwin } = useParcelTwinState(parcel?.id);
  const { data: entityData } = usePartyEntities({ limit: 50 });
  const parcelGroups = useMapperParcelGroups();
  const createParcelGroup = useCreateMapperParcelGroup();
  const updateParcelGroup = useUpdateMapperParcelGroup();
  const taxonomy = useInfrastructureTaxonomy((parcel?.land_category as LandCategory | null) || null, { enabled: !!parcel?.id });
  const { data: templates } = useInfraTemplates();
  const { data: memoriesData } = useEntityMemories(parcel?.id ?? '');
  const scanFarm = useFarmInfrastructureScan();

  const entities = entityData?.entities ?? [];
  const groups = parcelGroups.data ?? [];
  const memories = (memoriesData?.memories ?? []) as EntityMemory[];
  const infra = infraItems ?? [];

  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [entitySearch, setEntitySearch] = useState('');
  const [entityDropdownOpen, setEntityDropdownOpen] = useState(false);
  const [groupingOpen, setGroupingOpen] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [templateSectionOpen, setTemplateSectionOpen] = useState(false);
  const [activeTemplateIds, setActiveTemplateIds] = useState<string[]>([]);

  useEffect(() => {
    if (parcel) {
      setActiveTemplateIds(parcel.active_template_ids ?? []);
    }
  }, [parcel]);

  if (!parcel || !parcelInspectorOpen) return null;

  const linkedEntity = parcel.linked_entity_id
    ? entities.find(e => e.id === parcel.linked_entity_id)
    : null;

  const filteredEntities = entitySearch
    ? entities.filter(e => e.name.toLowerCase().includes(entitySearch.toLowerCase()))
    : entities;
  const overlayOpacity = geeOverlay?.parcel_id === parcel.id ? geeOverlay.opacity : 0.65;
  const ndviOverlayForParcel = geeOverlay?.parcel_id === parcel.id && geeOverlay.kind === 'gee-ndvi' ? geeOverlay : null;
  const ndviPending = ndviOverlayForParcel?.status === 'pending';
  const declaredTypes = useMemo(
    () => (taxonomy.data?.checklist?.sections ?? [])
      .flatMap((section) => section.templates.map((template) => template.key))
      .slice(0, 40),
    [taxonomy.data?.checklist?.sections]
  );
  const scanPending = scanFarm.isPending;
  const reviewSuggestionCount = infra.filter((item) => item.source === 'agent:gee' && item.status === 'review').length;
  const farmSaved = hasSavedFarm(parcel.id);
  const parcelFarmGroups = groups.filter((group) => (group.parcel_ids || []).includes(parcel.id));

  const handleSaveName = () => {
    if (editName.trim() && editName.trim() !== parcel.farm_name) {
      updateParcel.mutate(
        { id: parcel.id, farm_name: editName.trim() },
        {
          onSuccess: (updatedParcel) => {
            setActiveParcel(updatedParcel);
            if (farmSaved) syncSavedFarm(updatedParcel);
            toast.success('Parcel renamed');
          },
        }
      );
    }
    setEditing(false);
  };

  const handleAssignEntity = (entityId: string) => {
    assignEntity.mutate(
      { parcelId: parcel.id, entity_id: entityId },
      {
        onSuccess: (result) => {
          setActiveParcel(result.parcel);
          setActiveFarm(result.parcel.id, result.parcel.linked_entity_id || null, workspace.active_farm_group_id || null);
          if (farmSaved) syncSavedFarm(result.parcel);
        },
      }
    );
    setEntityDropdownOpen(false);
    toast.success('Entity assigned');
  };

  const handleToggleTemplate = (templateId: string) => {
    const next = activeTemplateIds.includes(templateId)
      ? activeTemplateIds.filter(id => id !== templateId)
      : [...activeTemplateIds, templateId];
    setActiveTemplateIds(next);
    setActiveParcel({ ...parcel, active_template_ids: next });
    updateParcel.mutate(
      { id: parcel.id, active_template_ids: next },
      {
        onSuccess: (updatedParcel) => {
          setActiveParcel(updatedParcel);
          if (farmSaved) syncSavedFarm(updatedParcel);
        },
        onError: (err) => {
          setActiveTemplateIds(parcel.active_template_ids ?? []);
          setActiveParcel(parcel);
          toast.error('Template update failed', { description: String(err) });
        },
      }
    );
  };

  const handleSyncToPim = () => {
    if (!parcel.linked_entity_id) {
      window.dispatchEvent(new CustomEvent(ENTITY_REMINDER_EVENT, {
        detail: {
          message: 'Link a legal entity before syncing this parcel to PIM.',
        },
      }));
      setEntityDropdownOpen(true);
      toast.info('Link a legal entity before syncing this parcel to PIM.');
      return;
    }
    syncToPim.mutate(parcel.id, {
      onSuccess: () => toast.success('Parcel synced to PIM'),
      onError: (err) => toast.error('Sync failed', { description: String(err) }),
    });
  };

  const handleCreateGroup = () => {
    const groupName = newGroupName.trim();
    if (!groupName) return;
    const existingGroup = groups.find((group) => group.name.trim().toLowerCase() === groupName.toLowerCase());
    if (existingGroup) {
      handleAddParcelToGroup(existingGroup.id);
      setNewGroupName('');
      setGroupingOpen(false);
      return;
    }

    createParcelGroup.mutate({
      name: groupName,
      linked_entity_id: parcel.linked_entity_id || workspace.active_entity_id || null,
      parcel_ids: [parcel.id],
      source: 'parcel_inspector',
    }, {
      onSuccess: (group) => {
        if (!farmSaved) {
          saveFarm(parcel);
        }
        setParcelLayerScope('farm_only');
        setActiveFarm(parcel.id, group.linked_entity_id || parcel.linked_entity_id || null, group.id);
        setActiveFarmGroup(group.id, group.linked_entity_id || parcel.linked_entity_id || null);
        setNewGroupName('');
        setGroupingOpen(false);
        toast.success(`Parcel added to ${group.name}`);
      },
      onError: (err) => {
        toast.error('Could not create the farm', { description: String(err) });
      },
    });
  };

  const handleAddParcelToGroup = (groupId: string) => {
    const group = groups.find((item) => item.id === groupId);
    if (!group) return;
    updateParcelGroup.mutate({
      id: groupId,
      parcel_ids: Array.from(new Set([...(group.parcel_ids || []), parcel.id])),
      linked_entity_id: group.linked_entity_id || parcel.linked_entity_id || workspace.active_entity_id || null,
    }, {
      onSuccess: (updatedGroup) => {
        if (!farmSaved) {
          saveFarm(parcel);
        }
        setParcelLayerScope('farm_only');
        setActiveFarm(parcel.id, updatedGroup.linked_entity_id || parcel.linked_entity_id || null, updatedGroup.id);
        setActiveFarmGroup(updatedGroup.id, updatedGroup.linked_entity_id || parcel.linked_entity_id || null);
        toast.success(`Parcel added to ${updatedGroup.name}`);
      },
      onError: (err) => {
        toast.error('Could not add the parcel to the farm', { description: String(err) });
      },
    });
  };

  const handleRemoveParcelFromGroup = (groupId: string) => {
    const group = groups.find((item) => item.id === groupId);
    if (!group) return;
    updateParcelGroup.mutate({
      id: groupId,
      parcel_ids: (group.parcel_ids || []).filter((parcelId) => parcelId !== parcel.id),
    }, {
      onSuccess: (updatedGroup) => {
        if (workspace.active_farm_group_id === updatedGroup.id && updatedGroup.parcel_ids.length === 0) {
          setParcelLayerScope('viewport');
          setActiveFarm(parcel.id, parcel.linked_entity_id || null, null);
          setActiveFarmGroup(null, parcel.linked_entity_id || null);
        }
        toast.success(`Parcel removed from ${group.name}`);
      },
      onError: (err) => {
        toast.error('Could not remove the parcel from the farm', { description: String(err) });
      },
    });
  };

  const handleScanParcel = async () => {
    try {
      await scanFarm.runScan({
        parcels: [{ ...parcel, active_template_ids: activeTemplateIds }],
        entityId: parcel.linked_entity_id || undefined,
        landCategory: (parcel.land_category as LandCategory | null) || null,
        declaredTypes,
        replaceExisting: true,
      });
      toast.success('Parcel scan started', {
        description: 'Scanning this parcel for infrastructure and field suggestions. Review items will open automatically when ready.',
      });
    } catch (err) {
      toast.error('Parcel scan failed', { description: String(err) });
    }
  };

  const handleRunParcelNdvi = () => {
    const parcelLabel = parcel.farm_name || parcel.sg_number;
    const overlayTitle = `${parcelLabel} NDVI`;
    const overlaySubtitle = 'Sentinel-2 NDVI overlay for the most recent 90 days';

    setGeeOverlay({
      id: `gee-ndvi:${parcel.id}`,
      kind: 'gee-ndvi',
      job_id: null,
      parcel_id: parcel.id,
      title: overlayTitle,
      subtitle: overlaySubtitle,
      tile_url: null,
      status: 'pending',
      error: null,
      opacity: overlayOpacity,
    });

    createGeeJob.mutate(
      {
        job_type: 'ndvi_classification',
        parcel_id: parcel.id,
        params: {
          source: 'parcel_inspector',
          active_template_ids: activeTemplateIds,
        },
      },
      {
        onSuccess: (result) => {
          setGeeOverlay({
            id: result.job_id,
            kind: 'gee-ndvi',
            job_id: result.job_id,
            parcel_id: parcel.id,
            title: overlayTitle,
            subtitle: overlaySubtitle,
            tile_url: result.tile_url ?? null,
            status: result.status,
            error: null,
            opacity: overlayOpacity,
          });

          if (result.status === 'completed' && result.tile_url) {
            toast.success('NDVI overlay loaded', {
              description: 'Imagery is now visible on the map. Use the overlay card to adjust opacity.',
            });
          } else {
            toast.success('NDVI analysis started', {
              description: 'Earth Engine is preparing the parcel overlay.',
            });
          }
        },
        onError: (err) => {
          setGeeOverlay({
            id: `gee-ndvi:${parcel.id}`,
            kind: 'gee-ndvi',
            job_id: null,
            parcel_id: parcel.id,
            title: overlayTitle,
            subtitle: overlaySubtitle,
            tile_url: null,
            status: 'failed',
            error: String(err),
            opacity: overlayOpacity,
          });
          toast.error('NDVI analysis failed', { description: String(err) });
        },
      }
    );
  };

  const handleClose = () => {
    setParcelInspectorOpen(false);
  };

  const handleToggleSavedFarm = () => {
    if (farmSaved) {
      removeFarm(parcel.id);
      toast.success('Farm removed from workspace');
      return;
    }
    saveFarm(parcel);
    toast.success('Farm saved to workspace');
  };

  const handleDeleteParcel = () => {
    if (!confirm(`Delete farm "${parcel.farm_name || parcel.sg_number}"? This cannot be undone.`)) return;
    deleteParcel.mutate(parcel.id, {
      onSuccess: () => {
        removeFarm(parcel.id);
        setActiveParcel(null);
        setParcelInspectorOpen(false);
        toast.success('Farm deleted');
      },
      onError: (err) => {
        toast.error('Farm delete failed', { description: String(err) });
      },
    });
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 200, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 200, opacity: 0 }}
        transition={{ type: 'spring', stiffness: 300, damping: 28 }}
        className="border-t border-gray-200 bg-white/95 backdrop-blur-md max-h-[55vh] overflow-y-auto"
      >
        {/* Header */}
        <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100 sticky top-0 bg-white/95 backdrop-blur-md z-10">
          <div className="w-8 h-8 rounded-lg bg-teal-100 flex items-center justify-center">
            <Landmark className="w-4 h-4 text-teal-700" />
          </div>
          <div className="flex-1 min-w-0">
            {editing ? (
              <div className="flex items-center gap-1">
                <input
                  autoFocus
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSaveName()}
                  className="text-sm font-medium text-gray-900 bg-transparent border-b border-teal-400 outline-none w-full"
                />
                <button onClick={handleSaveName} className="p-1 text-teal-600 hover:text-teal-700">
                  <Save className="w-3.5 h-3.5" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-1.5">
                <span className="text-sm font-medium text-gray-900 truncate">
                  {parcel.farm_name || parcel.sg_number}
                </span>
                <button
                  onClick={() => { setEditName(parcel.farm_name || ''); setEditing(true); }}
                  className="p-0.5 text-gray-300 hover:text-gray-500 transition-colors"
                >
                  <Edit3 className="w-3 h-3" />
                </button>
              </div>
            )}
            <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
              <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium bg-teal-100 text-teal-700">
                {parcel.sg_number}
              </span>
              {parcel.province && (
                <span className="text-[10px] text-gray-400">{parcel.province}</span>
              )}
              {parcel.municipality && (
                <span className="text-[10px] text-gray-400">· {parcel.municipality}</span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1">
            <motion.button
              whileTap={{ scale: 0.9 }}
              onClick={handleDeleteParcel}
              disabled={deleteParcel.isPending}
              className="p-1.5 rounded-md hover:bg-rose-50 text-gray-400 hover:text-rose-500 transition-colors disabled:opacity-40"
              title="Delete farm"
            >
              <Trash2 className="w-4 h-4" />
            </motion.button>
            <motion.button
              whileTap={{ scale: 0.9 }}
              onClick={handleClose}
              className="p-1.5 rounded-md hover:bg-gray-100 text-gray-400 transition-colors"
            >
              <X className="w-4 h-4" />
            </motion.button>
          </div>
        </div>

        {/* Area */}
        <div className="px-4 py-3">
          <div className="text-[10px] text-gray-400 font-medium uppercase tracking-wider">Area</div>
          <div className="text-sm font-semibold text-gray-800">{parcel.area_ha.toFixed(1)} ha</div>
        </div>

        {/* Entity Link */}
        <div className="px-4 py-2 border-t border-gray-100">
          <div className="text-[10px] text-gray-400 font-medium uppercase tracking-wider mb-1.5">Legal Entity</div>
          {linkedEntity ? (
            <div className="flex items-center gap-2 text-xs bg-blue-50 rounded-md px-2 py-1.5">
              <Building2 className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
              <span className="text-blue-700 font-medium truncate flex-1">{linkedEntity.name}</span>
              <button
                onClick={() => setEntityDropdownOpen(!entityDropdownOpen)}
                className="text-[10px] text-blue-500 hover:text-blue-700 font-medium"
              >
                Change
              </button>
            </div>
          ) : (
            <button
              onClick={() => setEntityDropdownOpen(!entityDropdownOpen)}
              className="w-full text-left text-xs text-gray-400 hover:text-gray-600 bg-gray-50 rounded-md px-2 py-1.5 flex items-center gap-1.5 transition-colors"
            >
              <Link2 className="w-3.5 h-3.5" />
              Select entity...
            </button>
          )}
          <AnimatePresence>
            {entityDropdownOpen && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden mt-1.5"
              >
                <div className="bg-white border border-gray-200 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                  <div className="sticky top-0 bg-white p-1.5 border-b border-gray-100">
                    <div className="flex items-center gap-1.5 bg-gray-50 rounded-md px-2 py-1">
                      <Search className="w-3 h-3 text-gray-400" />
                      <input
                        autoFocus
                        value={entitySearch}
                        onChange={(e) => setEntitySearch(e.target.value)}
                        placeholder="Search entities..."
                        className="text-xs bg-transparent outline-none flex-1"
                      />
                    </div>
                  </div>
                  {filteredEntities.map(entity => (
                    <button
                      key={entity.id}
                      onClick={() => handleAssignEntity(entity.id)}
                      className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 flex items-center gap-2 transition-colors"
                    >
                      <Building2 className="w-3 h-3 text-gray-400" />
                      <span className="truncate">{entity.name}</span>
                    </button>
                  ))}
                  {filteredEntities.length === 0 && (
                    <p className="text-xs text-gray-300 text-center py-3">No entities found</p>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div className="px-4 py-2 border-t border-gray-100">
          <div className="flex items-center justify-between gap-2 mb-1.5">
            <div className="text-[10px] text-gray-400 font-medium uppercase tracking-wider">Farm Membership</div>
            <button
              type="button"
              onClick={() => setGroupingOpen((current) => !current)}
              className="text-[10px] font-medium text-emerald-600 hover:text-emerald-700"
            >
              {groupingOpen ? 'Close' : 'Add to farm'}
            </button>
          </div>
          <div className="rounded-md bg-gray-50 px-2 py-2">
            {parcelFarmGroups.length === 0 ? (
              <div className="text-[11px] text-gray-500">
                This parcel is not part of any farm yet. Add it to an existing farm or create a new one below.
              </div>
            ) : (
              <div className="space-y-1.5">
                {parcelFarmGroups.map((group) => (
                  <div key={group.id} className="flex items-center gap-2 rounded-md bg-white px-2 py-1.5 text-xs">
                    <Building2 className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" />
                    <button
                      type="button"
                      onClick={() => {
                        setParcelLayerScope('farm_only');
                        setActiveFarm(parcel.id, group.linked_entity_id || parcel.linked_entity_id || null, group.id);
                        setActiveFarmGroup(group.id, group.linked_entity_id || parcel.linked_entity_id || null);
                      }}
                      className="flex-1 truncate text-left font-medium text-gray-800"
                    >
                      {group.name}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleRemoveParcelFromGroup(group.id)}
                      className="text-[10px] font-medium text-rose-600 hover:text-rose-700"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <AnimatePresence>
            {groupingOpen && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                <div className="mt-2 space-y-2">
                  <div className="space-y-1.5">
                    {groups.filter((group) => !parcelFarmGroups.some((item) => item.id === group.id)).map((group) => (
                      <button
                        key={group.id}
                        type="button"
                        onClick={() => handleAddParcelToGroup(group.id)}
                        className="flex w-full items-center gap-2 rounded-md border border-gray-200 bg-white px-2 py-1.5 text-left text-xs text-gray-700 transition-colors hover:border-emerald-200 hover:bg-emerald-50"
                      >
                        <Building2 className="w-3.5 h-3.5 text-gray-400" />
                        <span className="flex-1 truncate">{group.name}</span>
                        <span className="text-[10px] text-gray-400">{group.parcel_ids.length}</span>
                      </button>
                    ))}
                    {groups.filter((group) => !parcelFarmGroups.some((item) => item.id === group.id)).length === 0 && (
                      <div className="rounded-md bg-gray-50 px-2 py-2 text-[11px] text-gray-500">
                        No other farms available yet. Create a new one below.
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <input
                      value={newGroupName}
                      onChange={(event) => setNewGroupName(event.target.value)}
                      placeholder="Create new farm..."
                      className="flex-1 rounded-md border border-gray-200 px-2 py-1.5 text-xs outline-none focus:border-emerald-300"
                    />
                    <button
                      type="button"
                      onClick={handleCreateGroup}
                      disabled={!newGroupName.trim() || createParcelGroup.isPending}
                      className="rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-emerald-700 disabled:opacity-40"
                    >
                      Create
                    </button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Active Templates / Forms */}
        <div className="px-4 py-2 border-t border-gray-100">
          <button
            onClick={() => setTemplateSectionOpen(!templateSectionOpen)}
            className="w-full flex items-center gap-2 text-[10px] text-gray-400 font-medium uppercase tracking-wider mb-1.5"
          >
            <span className="flex-1 text-left">Forms / Templates</span>
            {activeTemplateIds.length > 0 && (
              <span className="text-[10px] bg-teal-100 text-teal-600 px-1.5 py-0.5 rounded-full font-medium normal-case">
                {activeTemplateIds.length} active
              </span>
            )}
            <motion.div animate={{ rotate: templateSectionOpen ? 0 : -90 }} transition={{ duration: 0.2 }}>
              <ChevronDown className="w-3 h-3" />
            </motion.div>
          </button>
          <AnimatePresence>
            {templateSectionOpen && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                <div className="space-y-0.5 max-h-32 overflow-y-auto">
                  {(templates ?? []).map(tmpl => {
                    const isActive = activeTemplateIds.includes(tmpl.id);
                    return (
                      <button
                        key={tmpl.id}
                        onClick={() => handleToggleTemplate(tmpl.id)}
                        className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-xs transition-colors ${
                          isActive ? 'bg-teal-50 border border-teal-200' : 'hover:bg-gray-50 border border-transparent'
                        }`}
                      >
                        <div className={`w-4 h-4 rounded flex items-center justify-center flex-shrink-0 ${
                          isActive ? 'bg-teal-500 text-white' : 'bg-gray-200 text-transparent'
                        }`}>
                          <Check className="w-3 h-3" />
                        </div>
                        <span className="truncate text-gray-700">{tmpl.name}</span>
                        {tmpl.entity_type && (
                          <span className="text-[10px] text-gray-400 ml-auto flex-shrink-0">{tmpl.entity_type}</span>
                        )}
                      </button>
                    );
                  })}
                  {(!templates || templates.length === 0) && (
                    <p className="text-xs text-gray-300 italic py-2">No infrastructure templates available</p>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Parcel twin summary */}
        <div className="px-4 py-2 border-t border-gray-100">
          <div className="text-[10px] text-gray-400 font-medium uppercase tracking-wider mb-1.5">Digital Twin</div>
          <div className="grid grid-cols-2 gap-2">
            <div className="rounded-md bg-emerald-50 px-2 py-2">
              <div className="text-[10px] text-emerald-700 font-medium">Fields</div>
              <div className="text-sm font-semibold text-emerald-900">{parcelTwin?.fields?.length ?? 0}</div>
            </div>
            <div className="rounded-md bg-blue-50 px-2 py-2">
              <div className="text-[10px] text-blue-700 font-medium">Relationships</div>
              <div className="text-sm font-semibold text-blue-900">{parcelTwin?.relationships?.length ?? 0}</div>
            </div>
            <div className="rounded-md bg-amber-50 px-2 py-2">
              <div className="text-[10px] text-amber-700 font-medium">NDVI</div>
              <div className="text-xs font-semibold text-amber-900">
                {parcelTwin?.ndvi?.status ? parcelTwin.ndvi.status : 'Not generated'}
              </div>
            </div>
            <div className="rounded-md bg-slate-50 px-2 py-2">
              <div className="text-[10px] text-slate-700 font-medium">Telemetry</div>
              <div className="text-sm font-semibold text-slate-900">{parcelTwin?.telemetry?.linked_device_count ?? 0}</div>
            </div>
          </div>
          <div className="mt-2 text-[11px] text-gray-500">
            {parcelTwin?.weather?.available
              ? `${parcelTwin.weather.linked_feed_count} weather/API feed${parcelTwin.weather.linked_feed_count === 1 ? '' : 's'} linked`
              : 'No weather/API feeds linked yet'}
          </div>
        </div>

        {/* Infrastructure within parcel */}
        <div className="px-4 py-2 border-t border-gray-100">
          <div className="flex items-center justify-between gap-2 mb-1.5">
            <div className="text-[10px] text-gray-400 font-medium uppercase tracking-wider">
              Infrastructure ({infra.length})
            </div>
            {reviewSuggestionCount > 0 && (
              <span className="text-[10px] font-medium text-amber-700 bg-amber-100 rounded-full px-2 py-0.5">
                {reviewSuggestionCount} review
              </span>
            )}
          </div>
          {infra.length === 0 ? (
            <p className="text-xs text-gray-300 italic">No infrastructure in this parcel</p>
          ) : (
            <div className="space-y-0.5 max-h-36 overflow-y-auto">
              {infra.map(item => {
                const catColor = CATEGORY_COLORS[item.category as InfraCategoryType] || CATEGORY_COLORS.buildings;
                const deviceCount = item.linked_device_ids?.length ?? 0;
                const isReviewSuggestion = item.source === 'agent:gee' && item.status === 'review';
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => setSelectedInfrastructure(item)}
                    className="w-full flex items-center gap-2 text-xs bg-gray-50 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-emerald-50"
                  >
                    <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: catColor.hex }} />
                    <span className="text-gray-700 truncate flex-1">{item.name}</span>
                    {isReviewSuggestion && (
                      <span className="text-[10px] font-medium text-amber-700 bg-amber-100 rounded-full px-1.5 py-0.5">
                        Review
                      </span>
                    )}
                    <span className="text-[10px] text-gray-400">
                      {CATEGORY_LABELS[item.category as InfraCategoryType]?.split(' ')[0] || item.category}
                    </span>
                    {deviceCount > 0 && (
                      <span className="flex items-center gap-0.5 text-[10px] text-gray-400">
                        <Cpu className="w-3 h-3" /> {deviceCount}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* History */}
        <div className="px-4 py-2 border-t border-gray-100">
          <div className="text-[10px] text-gray-400 font-medium uppercase tracking-wider mb-1.5">History</div>
          {memories.length === 0 ? (
            <p className="text-xs text-gray-300 italic">No history recorded yet</p>
          ) : (
            <div className="space-y-1 max-h-44 overflow-y-auto">
              {memories.map(mem => {
                const Icon = MEMORY_TYPE_ICONS[mem.memory_type] ?? Calendar;
                const detail = mem.content?.detail ?? '';
                return (
                  <div key={mem.id} className="flex items-start gap-2 text-xs bg-gray-50 rounded-md px-2 py-1.5">
                    <Icon className="w-3.5 h-3.5 text-gray-400 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="text-gray-700 font-medium truncate">{mem.content?.title}</span>
                        <span className="text-[10px] text-gray-400 flex-shrink-0">{formatRelativeTime(mem.timestamp)}</span>
                      </div>
                      {detail && (
                        <p className="text-[11px] text-gray-400 mt-0.5 line-clamp-2">
                          {detail.length > 100 ? `${detail.slice(0, 100)}…` : detail}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="px-4 py-3 flex flex-wrap items-center gap-2 border-t border-gray-100 sticky bottom-0 bg-white/95 backdrop-blur-md">
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={handleToggleSavedFarm}
            className={`flex items-center justify-center gap-1.5 py-2 px-3 text-xs font-medium rounded-md transition-colors ${
              farmSaved
                ? 'text-emerald-700 bg-emerald-50 hover:bg-emerald-100'
                : 'text-white bg-emerald-600 hover:bg-emerald-700'
            }`}
          >
            <Save className="w-3.5 h-3.5" />
            {farmSaved ? 'Remove Saved' : 'Save Farm'}
          </motion.button>
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={handleScanParcel}
            disabled={scanPending}
            className="flex items-center justify-center gap-1.5 py-2 px-3 text-xs font-medium rounded-md text-amber-700 bg-amber-50 hover:bg-amber-100 disabled:opacity-40 transition-colors"
          >
            <Search className={`w-3.5 h-3.5 ${scanPending ? 'animate-pulse' : ''}`} />
            Scan Parcel
          </motion.button>
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={handleRunParcelNdvi}
            disabled={createGeeJob.isPending || ndviPending}
            className="flex items-center justify-center gap-1.5 py-2 px-3 text-xs font-medium rounded-md text-emerald-700 bg-emerald-50 hover:bg-emerald-100 disabled:opacity-40 transition-colors"
          >
            <Leaf className={`w-3.5 h-3.5 ${createGeeJob.isPending || ndviPending ? 'animate-pulse' : ''}`} />
            {ndviPending ? 'NDVI Pending' : ndviOverlayForParcel?.status === 'completed' ? 'Refresh NDVI' : 'NDVI Layer'}
          </motion.button>
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={handleSyncToPim}
            disabled={syncToPim.isPending}
            className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-teal-600 text-white text-xs font-medium rounded-md hover:bg-teal-700 disabled:opacity-40 transition-colors"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${syncToPim.isPending ? 'animate-spin' : ''}`} />
            Sync to PIM
          </motion.button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
